import type { NitroFetchOptions, NitroFetchRequest } from "nitropack";
import _set from "lodash/set";
import { safeInternalPath } from "~/utils/safeInternalPath";

// Helper functions to get config values (must be called inside Nuxt context)
export const getBaseUrl = () => {
  const config = useRuntimeConfig();
  const isProduction = config.public.nodeEnv === "production";
  return isProduction ? config.public.apiUrlProd : config.public.apiUrlDev;
};

export const getAppCode = () => {
  const config = useRuntimeConfig();
  return config.public.appCode;
};

// Internal helper
const getConfig = () => {
  return { BASE_URL: getBaseUrl(), APP_CODE: getAppCode() };
};

export const getAuthToken = () => {
  const cookieVal = useCookie('token').value || useCookie('auth_token').value || useCookie('accessToken').value;
  if (cookieVal) return cookieVal;
  const appToken = useAppCookie('token').value;
  if (appToken) return appToken;
  if (typeof window !== 'undefined' && window.localStorage) {
    const raw = localStorage.getItem('token') || localStorage.getItem('accessToken') || localStorage.getItem('auth_token');
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed) return parsed;
      } catch (e) {
        return raw;
      }
    }
  }
  return '';
};

const errorHandler = (error: any, config: Record<string, any>) => {
  const status = error?.statusCode || error?.status || error?.response?.status || error?.data?.statusCode;
  if (process.client && status === 401) {
    const route = useRoute();
    const publicRoutes = ["login", "login-otp", "register", "register-otp"];
    if (!publicRoutes.includes(String(route.name))) {
      // A 401 means the stored token is dead. Drop it, otherwise /login sees
      // userId+token, bounces back to `next`, which 401s again -> endless loop.
      try {
        const token = useAppCookie("token");
        const userId = useAppCookie("userId");
        if (token.value || userId.value) {
          token.value = null as any;
          userId.value = null as any;
          localStorage.removeItem("profileComplete");
        }
      } catch (e) {
        /* keep going: the redirect below still works */
      }
      navigateTo({
        path: "/login",
        query: { next: safeInternalPath(route.fullPath, "/") }
      }, { replace: true });
    }
  }
  // @ts-ignore
  const fieldMessage = error?.data?.errors
    ?.map((e: any) => e.field + ":" + e.message)
    .join(" , ");
  const codeMessages: Record<string, string> = {
    INSUFFICIENT_BALANCE: "موجودی کیف پول کافی نیست",
    SLOT_ALREADY_BOOKED: "این سانس قبلاً رزرو شده است",
  };
  const backendMessage = error?.data?.message;
  const persianMessage = typeof backendMessage === "string" && /[\u0600-\u06FF]/.test(backendMessage)
    ? backendMessage
    : "";
  const message = fieldMessage
    || codeMessages[error?.data?.code]
    || persianMessage
    || "در انجام درخواست خطایی رخ داد؛ لطفاً دوباره تلاش کنید";
  // @ts-ignore
  if (!config?.headers?.["no-error"]) {
    const { $toast } = useNuxtApp();
    // @ts-ignore
    $toast?.error(message);
  }
  return { error };
};

export const useGet = async <T>(
  URL: string,
  config:
    | NitroFetchOptions<NitroFetchRequest, "get">
    | {
    headers?: Record<string, any>;
  } = {},
  controller: any = undefined
): Promise<{
  result?: T;
  error?: any;
}> => {
  const { BASE_URL, APP_CODE } = getConfig();
  const signal = controller?.signal;
  const token = getAuthToken();
  _set(config, ["headers", "Authorization"], token || "");
  _set(config, ["headers", "app-code"], APP_CODE);
  _set(config, ["headers", "no-error"], true);
  try {
    const result = await $fetch<T>(URL, {
      baseURL: BASE_URL,
      method: "GET",
      ...config,
      ...(signal ? { signal } : {})
    });
    return { result };
  } catch (error) {
    return errorHandler(error, config);
  }
};

export const useDelete = async <T>(
  URL: string,
  config: NitroFetchOptions<NitroFetchRequest, "delete"> = {}
): Promise<{
  result?: T;
  error?: any;
}> => {
  const { BASE_URL, APP_CODE } = getConfig();
  const token = getAuthToken();
  _set(config, ["headers", "Authorization"], token || "");
  _set(config, ["headers", "app-code"], APP_CODE);
  try {
    const result = await $fetch<T>(URL, {
      baseURL: BASE_URL,
      method: "DELETE",
      ...config
    });
    return { result };
  } catch (error) {
    return errorHandler(error, config);
  }
};

export const usePost = async <T, R = T>(
  URL: string,
  data: R,
  config: NitroFetchOptions<NitroFetchRequest, "post"> = {}
): Promise<{
  result?: T;
  error?: any;
}> => {
  const { BASE_URL, APP_CODE } = getConfig();
  const token = getAuthToken();
  _set(config, ["headers", "Authorization"], token || "");
  _set(config, ["headers", "app-code"], APP_CODE);
  try {
    const result = await $fetch<T>(URL, {
      baseURL: BASE_URL,
      method: "POST",
      body: data as Record<string, any>,
      ...config
    });
    return { result };
  } catch (error) {
    return errorHandler(error, config);
  }
};

export const usePut = async <T, R = T>(
  URL: string,
  data: Partial<R>,
  config: NitroFetchOptions<NitroFetchRequest, "put"> = {}
): Promise<{
  result?: T;
  error?: any;
}> => {
  const { BASE_URL, APP_CODE } = getConfig();
  const token = getAuthToken();
  _set(config, ["headers", "Authorization"], token || "");
  _set(config, ["headers", "app-code"], APP_CODE);
  try {
    const result = await $fetch<T>(URL, {
      baseURL: BASE_URL,
      method: "PUT",
      body: data as Record<string, any>,
      ...config
    });
    return { result };
  } catch (error) {
    return errorHandler(error, config);
  }
};
